import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { content } from "../content/content";

const NAV_LINKS = [
  { label: "Our Stories", href: "#stories" },
  { label: "Our Centres", href: "#centers" },
  { label: "Who We Are", href: "#team" },
  { label: "Gallery", href: "/gallery" },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";
  // Off the home route there's no full-height hero, so the nav is always solid.
  const solid = scrolled || !onHome;

  useEffect(() => {
    const onScroll = () => {
      // Solid treatment kicks in once we've left the hero behind
      setScrolled(window.scrollY > window.innerHeight * 0.7);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      setMobileOpen(false);
      // Route links (e.g. /gallery) fall through to react-router <Link>.
      if (!href.startsWith("#")) {
        return;
      }
      e.preventDefault();
      if (onHome) {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      } else {
        // Route home first, then scroll to the target section on arrival.
        navigate("/", { state: { scrollTo: href } });
      }
    },
    [onHome, navigate],
  );

  return (
    <>
      <header
        className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ${
          solid
            ? "bg-warm-white/92 shadow-[0_1px_0_rgba(26,26,46,0.06)] backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-10">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt=""
              className="h-8 w-auto"
              aria-hidden="true"
            />
            <span
              data-edit-id="content.nav.brand"
              className={`font-display text-lg font-medium tracking-tight transition-colors duration-500 ${
                solid ? "text-ink" : "text-white"
              }`}
              style={
                solid ? undefined : { textShadow: "0 1px 12px rgba(0,0,0,0.4)" }
              }
            >
              {content.nav.brand}
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((link) => {
              const linkClass = `font-body text-sm font-medium tracking-wide transition-colors duration-300 hover:opacity-70 ${
                solid ? "text-ink-light" : "text-white/90"
              }`;
              const linkStyle = solid
                ? undefined
                : { textShadow: "0 1px 10px rgba(0,0,0,0.35)" };
              return link.href.startsWith("#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={linkClass}
                  style={linkStyle}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={linkClass}
                  style={linkStyle}
                >
                  {link.label}
                </Link>
              );
            })}
            <a
              data-edit-id="content.nav.cta"
              href="#contact"
              onClick={(e) => handleNavClick(e, "#contact")}
              className="rounded-2xl bg-saffron px-5 py-2 text-sm font-medium text-warm-white transition-colors duration-300 hover:bg-saffron-dark"
            >
              {content.nav.cta}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="relative z-50 flex h-10 w-10 items-center justify-center lg:hidden"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            <div className="flex w-5 flex-col gap-1.5">
              <span
                className={`block h-px transition-all duration-300 ${
                  mobileOpen
                    ? "translate-y-[3.5px] rotate-45 bg-ink"
                    : solid
                      ? "bg-ink"
                      : "bg-white"
                }`}
              />
              <span
                className={`block h-px transition-all duration-300 ${
                  mobileOpen
                    ? "-translate-y-[3.5px] -rotate-45 bg-ink"
                    : solid
                      ? "bg-ink"
                      : "bg-white"
                }`}
              />
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-8 bg-warm-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {NAV_LINKS.map((link) =>
              link.href.startsWith("#") ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="font-display text-3xl text-ink transition-opacity hover:opacity-60"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-display text-3xl text-ink transition-opacity hover:opacity-60"
                >
                  {link.label}
                </Link>
              ),
            )}
            <a
              data-edit-id="content.nav.ctaMobile"
              href="#contact"
              onClick={(e) => handleNavClick(e, "#contact")}
              className="mt-4 rounded-2xl bg-saffron px-8 py-3 text-lg font-medium text-warm-white"
            >
              {content.nav.ctaMobile}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
